import {
  Component,
  ChangeDetectionStrategy,
  inject,
  computed,
  signal,
} from '@angular/core';
import { MAT_BOTTOM_SHEET_DATA, MatBottomSheetRef } from '@angular/material/bottom-sheet';
import { SHARED_IMPORTS } from 'src/app/shared/standalone-imports';

/** Generic section item so this sheet works for both home and admin section shapes. */
export interface SectionSwitchItem {
  id: string;
  name: string;
}

export interface SectionSwitchBottomSheetData {
  sections: SectionSwitchItem[];
  currentSectionId: string | null;
}

/**
 * Section Switch Bottom Sheet Component
 * M3 Bottom Sheet pattern for mobile section switching.
 * モバイルでは mat-select ドロップダウンの代わりにこのボトムシートで選択する。
 * home / admin 系で共通利用する（呼び出し側で {id, name} に正規化して渡す）。
 */
@Component({
  selector: 'app-section-switch-bottom-sheet',
  templateUrl: './section-switch-bottom-sheet.component.html',
  styleUrls: ['./section-switch-bottom-sheet.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [...SHARED_IMPORTS],
})
export class SectionSwitchBottomSheetComponent {
  private readonly bottomSheetRef = inject(MatBottomSheetRef<SectionSwitchBottomSheetComponent>);
  readonly data = inject<SectionSwitchBottomSheetData>(MAT_BOTTOM_SHEET_DATA);

  /** Search query for filtering sections */
  readonly searchQuery = signal('');

  /** Filtered sections based on search query */
  readonly filteredSections = computed(() => {
    const query = this.searchQuery().toLowerCase().trim();
    const sections = this.data.sections;
    if (!query) return sections;
    return sections.filter(s => (s.name ?? '').toLowerCase().includes(query));
  });

  /** Whether a section is the currently selected one */
  isCurrent(section: SectionSwitchItem): boolean {
    return !!this.data.currentSectionId && section.id === this.data.currentSectionId;
  }

  /** Select a section and close the sheet */
  selectSection(section: SectionSwitchItem): void {
    this.bottomSheetRef.dismiss(section);
  }

  /** Close the sheet without selection */
  close(): void {
    this.bottomSheetRef.dismiss();
  }

  /** Update search query */
  onSearchInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.searchQuery.set(input.value);
  }
}
